import {addToCartConfig} from '@udemy/cart';
import {getOSName} from '@udemy/shared-utils';
import {udLink} from '@udemy/ud-data';

export const enum VoucherProviderTypes {
    CERTIFICATION_PROVIDER_COMPTIA = 1,
    CERTIFICATION_PROVIDER_AWS = 2,
    CERTIFICATION_PROVIDER_MICROSOFT = 3,
    CERTIFICATION_PROVIDER_LINUX_FOUNDATION = 4,
}

export const getConfig = (
    gettext: (msgid: string) => string,
    interpolate: (template: string, source: unknown, isSourceObject?: boolean) => string,
    ngettext: (msgid: string, msgidPlural: string, count: number) => string,
) => {
    const defaultConfirmationPurchaseSettings = {
        leadText: gettext('Thank you for your order!'),
        moreInfo: gettext(
            'The course will be delivered to your Udemy account as soon as we receive payment confirmation ' +
                'from your bank. Once the course is delivered, you will be notified by email.',
        ),
    };

    const strings = {
        checkout: gettext('Checkout'),
        hideCodes: gettext('Hide Codes'),
        insertCode: gettext('Insert Code'),
        discountStatusText: {
            applied: gettext('<b>%(couponCode)s</b> is applied'),
            invalid: gettext('%(couponCode)s is not valid'),
            expired: gettext('%(couponCode)s has expired'),
            sold_out: gettext('%(couponCode)s has sold out'),
            unused: gettext('%(couponCode)s is not applied'),
        },
        shoppingCart: gettext('Shopping Cart'),
        emptyCartMessage: gettext(
            "<strong>Your cart is empty</strong> – let's change that. Time to learn some new skills!",
        ),
        learnersAreViewing: gettext('Learners are viewing'),
        popularTopics: gettext('Popular Topics'),
        popularTopicsItems: {
            generativeAI: gettext('Generative AI'),
            itCertifications: gettext('IT Certifications'),
            dataScience: gettext('Data Science'),
            chatGPT: gettext('ChatGPT'),
            promptEngineering: gettext('Prompt Engineering'),
            microsoftExcel: gettext('Microsoft Excel'),
            largeLanguageModels: gettext('Large Language Models'),
            machineLearning: gettext('Machine Learning'),
            aiAgents: gettext('AI Agents'),
        },
        certificationVoucher: {
            /** GQL providerDetails.name (e.g. "AWS") → localized "AWS Certification". */
            providerLabel: (issuerName: string) =>
                interpolate(gettext('%(provider)s Certification'), {provider: issuerName}, true),
            /** Fallback for cart API buyables that only expose certification_provider id. */
            providerNames: {
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_COMPTIA]:
                    gettext('CompTIA Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_AWS]: gettext('AWS Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_MICROSOFT]:
                    gettext('Microsoft Certification'),
                [VoucherProviderTypes.CERTIFICATION_PROVIDER_LINUX_FOUNDATION]: gettext(
                    'Linux Foundation Certification',
                ),
            } as Record<number, string>,
            validityText: (expiration_date_in_months: number) => {
                return interpolate(
                    ngettext(
                        'At least %s-month validity after purchase',
                        'At least %s-months validity after purchase',
                        expiration_date_in_months,
                    ),
                    [expiration_date_in_months],
                );
            },
            examFormatText: gettext('online or in-person exams'),
            voucherUpsellSectionTitle: gettext('Complete your certification prep'),
        },
    };

    const {errors, urls} = addToCartConfig;

    return {
        checkout: {
            types: ['cart', 'express'],
        },
        errors,
        shoppingList: {
            all: ['cart', 'saved_for_later', 'wishlist'],
            cartSublists: ['certification_voucher', 'course'] as const,
            sublistStrings: {
                title: {
                    certification_voucher: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Voucher in Cart', '%s Vouchers in Cart', items.length),
                            [items.length],
                        ),
                    course: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Course in Cart', '%s Courses in Cart', items.length),
                            [items.length],
                        ),
                },
            },
            listStrings: {
                title: {
                    cart: (items: unknown[]) =>
                        interpolate(
                            ngettext('%s Course in Cart', '%s Courses in Cart', items.length),
                            [items.length],
                        ),
                    saved_for_later: () => gettext('Saved for later'),
                    wishlist: () => gettext('Recently wishlisted'),
                },
                empty: {
                    cartPage: {
                        cart: gettext('Your cart is empty. Keep shopping to find a course!'),
                        saved_for_later: gettext("You haven't saved any courses for later."),
                        wishlist: gettext("You haven't added any courses to your wishlist."),
                    },
                    dropdown: {
                        cart: gettext('Your cart is empty.'),
                        wishlist: gettext('Your wishlist is empty.'),
                    },
                },
            },
        },
        strings,
        performanceMarkName: 'Cart.shopping-item-list-first-img-loaded',
        success: {
            cartSuccess: {
                leadText: gettext("You've successfully added"),
            },
            subscribe: {
                headerText: gettext('Jump right in'),
            },
            licensePurchase: {
                isConfirmation: false,
                leadText: gettext('Thanks for purchasing %(title)s!'),
                moreInfo: gettext(
                    'Invite people to take this course for free with this code! It can be used up to ' +
                        '%(remaining)s times. This redemption code is also included in the payment ' +
                        'confirmation email for your records.',
                ),
                redemptionCode: gettext('Your redemption code is:'),
            },
            multiplePurchase: {
                congratulations: gettext('Congratulations!'),
                headerText: gettext('Your New Purchases'),
                leadText: gettext("You've enrolled in %(count)s courses."),
                myCoursesUrl: '/home/my-courses',
            },
            singlePurchase: {
                headerText: gettext('Jump right in'),
            },
            licensePurchaseConfirmation: defaultConfirmationPurchaseSettings,
            singlePurchaseConfirmation: defaultConfirmationPurchaseSettings,
            mobilePurchaseConfirmation: Object.assign(
                {...defaultConfirmationPurchaseSettings},
                {
                    mobileImage: udLink.toImages(
                        `mobile/success/success_${getOSName()}_mobile.jpg`,
                    ),
                },
            ),
            multiplePurchaseConfirmation: Object.assign(
                {...defaultConfirmationPurchaseSettings},
                {
                    moreInfo: gettext(
                        'We are excited for you to start learning. The courses will be delivered ' +
                            'to your Udemy account as soon as we receive payment confirmation from ' +
                            'your bank. Once the courses are delivered, you will be notified by email ' +
                            'so you can get back to learning!',
                    ),
                },
            ),
        },
        urls,
    };
};
